import React, { useEffect, useState } from 'react';
import Papa from 'papaparse';
import { getDatabase, ref, onValue, off } from 'firebase/database';
import { useAuth } from 'hooks/useAuth';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getFirebaseBackend } from 'helpers/firebaseHelper';
import _ from 'lodash';
import axios from 'axios';
import { toast } from 'react-toastify';
import { MetaTags } from 'react-meta-tags';
import { useTranslation } from 'react-i18next';

import { GuidelineLayout } from './styles';
import ContentCard from './components/Content-Card';
import Breadcrumbs from '../../components/Breadcrumb';
import SendCard from './components/Send-Card';
import ButtonUploadCSV from './components/Button-Upload-CSV';
import PageLoading from './components/Page-Loading';
import {
  betaLanguageOptions,
  guidelineDeliveryTypeOptions,
  guidelineTypeOptions,
  monthNumberLabel,
} from '../../constants';
import { CheckIcon } from 'assets/icons';

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;
const lastYear = currentYear - 1;

const yearOptions = [
  { label: lastYear.toString(), value: lastYear },
  { label: currentYear.toString(), value: currentYear },
  { label: nextYear.toString(), value: nextYear },
];

const formConfig = {
  defaultValues: {
    guidelines: [
      {
        deliveryType: null,
        reviewLink: '',
        type: null,
        language: '',
        totalSize: 100,
        primaryKeyword: '',
        searchVolume: '',
        secondaryKeywords: '',
        productionDate: {
          month: monthNumberLabel.find((month) => month.value === new Date().getMonth() + 1),
          year: yearOptions.find((year) => year.value === new Date().getFullYear()),
        },
        unificationLinks: [],
      },
    ],
    notification: false,
  },
  shouldFocusError: true,
  criteriaMode: 'all',
  mode: 'onSubmit',
  reValidateMode: 'onChange',
};

const CreateGuideline = () => {
  const firebaseHelper = getFirebaseBackend();

  const form = useForm(formConfig);
  const { control, watch, handleSubmit } = form;
  const { t: translate } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const currentAccountId = user?.account;

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isGuidelineCreated, setIsGuidelineCreated] = useState(false);
  const [guidelineIds, setGuidelineIds] = useState([]);
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'guidelines',
  });

  const onSubmit = async (data) => {
    setIsSending(true);

    const firebaseToken = await firebaseHelper.getIdToken();
    const processedData = _.cloneDeep(data);

    processedData.guidelines = processedData.guidelines.map((guideline) => {
      return {
        ...guideline,
        deliveryType: guideline.deliveryType.value,
        reviewLink: guideline.reviewLink,
        type: guideline.type.value,
        language: guideline.language.value,
        primaryKeyword: guideline.primaryKeyword.toLowerCase(),
        secondaryKeywords: guideline.secondaryKeywords?.toLowerCase() || '',
        productionDate: {
          month: guideline.productionDate.month.value
            ? Number(guideline.productionDate.month.value)
            : Number(guideline.productionDate.month),
          year: guideline.productionDate.year.value
            ? Number(guideline.productionDate.year.value)
            : Number(guideline.productionDate.year),
        },
        unificationLinks: guideline.unificationLinks.map((link) => link.trim()),
        unificationLink: undefined, // unificationLink is not needed in the final data
      };
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/newGuideline`,
        {
          ...processedData,
          accountId: currentAccountId,
          development: process.env.REACT_APP_BUILD_TYPE !== 'production',
        },
        {
          headers: {
            Authorization: `Bearer ${firebaseToken}`,
          },
        },
      );
      setGuidelineIds(response.data?.guidelineIds ?? []);
      setIsSending(false);
      setIsPageLoading(true);
    } catch (error) {
      setIsSending(false);
      console.log(error);
      toast.error(
        error.response?.status === 422
          ? translate('ClickUp list not found for this account')
          : translate('An error occurred'),
      );
    }
  };

  const handleAddComponent = () => {
    append({
      deliveryType: null,
      reviewLink: '',
      type: null,
      language: '',
      totalSize: 100,
      primaryKeyword: '',
      searchVolume: '',
      secondaryKeywords: '',
      productionDate: {
        month: monthNumberLabel.find((month) => month.value === new Date().getMonth() + 1),
        year: yearOptions.find((year) => year.value === new Date().getFullYear()),
      },
    });
  };

  const handleDeleteComponent = (indexToDelete) => {
    remove(indexToDelete);
  };

  const handleFileChange = (event) => {
    const csv = event.target.files[0];

    if (csv.type !== 'text/csv') return;

    Papa.parse(csv, {
      header: true,
      skipEmptyLines: true,
      complete: function (results) {
        const parsedCSV = results.data;
        const newGuidelines = [];

        parsedCSV.forEach((guideline) => {
          // clean new line characters from string fields
          Object.keys(guideline).forEach((key) => {
            if (typeof guideline[key] === 'string') {
              guideline[key] = guideline[key].replace(/[\r\n]+/g, '').trim();
            }
          });

          const deliveryType = guidelineDeliveryTypeOptions.find(
            (type) => type.value.toLowerCase() === guideline?.deliveryType.toLowerCase(),
          );
          const guidelineType = guidelineTypeOptions.find(
            (type) => type.value.toLowerCase() === guideline?.type.toLowerCase(),
          );
          const language = betaLanguageOptions.find(
            (lang) => lang.value.toLowerCase() === guideline?.language.toLowerCase(),
          );
          const totalSize = guideline?.totalSize ? +guideline?.totalSize : null;
          const searchVolume = guideline?.searchVolume ? +guideline?.searchVolume : null;

          const productionMonth = monthNumberLabel.find((month) => {
            const cleanMonth =
              guideline.productionDateMonth.substring(0, 1) === '0'
                ? guideline.productionDateMonth.substring(1)
                : guideline.productionDateMonth;

            return month.value === Number(cleanMonth);
          });

          let productionYear = null;
          if (guideline.productionDateYear) {
            if (guideline.productionDateYear.length === 4) {
              productionYear = {
                value: Number(guideline.productionDateYear),
                label: guideline.productionDateYear,
              };
            } else {
              productionYear = { value: null, label: '' };
            }
          }

          newGuidelines.push({
            deliveryType: deliveryType || null,
            reviewLink: guideline?.reviewLink || null,
            type: guidelineType || null,
            language: language || null,
            totalSize: totalSize || null,
            primaryKeyword: guideline?.primaryKeyword || null,
            searchVolume: searchVolume || null,
            secondaryKeywords: guideline?.secondaryKeywords || null,
            productionDate: {
              month: productionMonth,
              year: productionYear,
            },
            unificationLinks: guideline?.unificationLinks
              ? guideline.unificationLinks.split(',').map((link) => link.trim())
              : [],
          });
        });

        replace(newGuidelines);
        setTimeout(() => {
          form.trigger();
          form.clearErrors();
        }, 100);
      },
    });
  };

  useEffect(() => {
    if (!isPageLoading || !guidelineIds.length) return;

    const db = getDatabase();
    const guidelinesRef = ref(db, `guidelines-v1/${currentAccountId}`);

    const handleValueChange = async (snapshot) => {
      const data = snapshot.val();
      if (!data) return;

      const guidelines = Object.entries(data).map(([guidelineId, guideline]) => ({
        id: guidelineId,
        ...guideline,
      }));

      if (guidelineIds.length === 1) {
        const guideline = guidelines.find((guideline) => guideline.id === guidelineIds[0]);
        if (guideline?.processing === false) navigate(`/guideline/${guidelineIds[0]}`);
      } else {
        const isProcessing = guidelines
          .filter((guideline) => guidelineIds.includes(guideline.id))
          .every((guideline) => !guideline.processing);

        if (!isProcessing) {
          setIsGuidelineCreated(true);
          setIsPageLoading(false);
          off(guidelinesRef);
        }
      }
    };

    onValue(guidelinesRef, handleValueChange);

    return () => {
      off(guidelinesRef, 'value', handleValueChange);
    };
  }, [isPageLoading]);

  return (
    <GuidelineLayout>
      <div className="page-content">
        <MetaTags>
          <title>{`${translate('Create Guideline')} | Ectools`}</title>
        </MetaTags>
        <Breadcrumbs
          title={translate('Guideline')}
          breadcrumbItem={translate('Create Guideline')}
        />
        {isGuidelineCreated && (
          <PageLoading
            title={translate('Guidelines created successfully!')}
            description={''}
            button={translate('See Guidelines')}
            onClick={() => navigate('/guidelines')}
          >
            <div style={{ scale: '0.5' }}>
              <CheckIcon />
            </div>
          </PageLoading>
        )}
        {isPageLoading && !isGuidelineCreated && (
          <PageLoading
            title={translate('Creating guideline')}
            description={translate(
              'This may take a few minutes, you can also wait for this process in the background and return later.',
            )}
            button={translate('Go back')}
            onClick={() => navigate('/guidelines')}
          />
        )}
        {!isPageLoading && !isGuidelineCreated && (
          <FormProvider {...form}>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const submit = handleSubmit(onSubmit);
                submit(e);
              }}
            >
              <div className="container-row">
                <div className="container-general-information">
                  <div className="header-general-information">
                    <div>
                      <button
                        aria-label="Back"
                        type="button"
                        id="back-button"
                        className="back-button"
                        onClick={() => window.history.back()}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M5.21875 7.33117H13.3334V8.6645H5.21875L8.79475 12.2405L7.85208 13.1832L2.66675 7.99783L7.85208 2.8125L8.79475 3.75517L5.21875 7.33117Z"
                            fill="#2D3748"
                          />
                        </svg>
                      </button>
                      <p>{translate('General Information')}</p>
                    </div>
                    <ButtonUploadCSV changeHandler={handleFileChange} />
                  </div>
                  <div className="container-cards">
                    {fields.map((field, index) => (
                      <ContentCard
                        key={field.id}
                        index={index}
                        canDelete={fields.length > 1}
                        onDelete={handleDeleteComponent}
                      />
                    ))}
                  </div>
                  <div className="button-add-new-text">
                    <button type="button" onClick={handleAddComponent}>
                      <i className="bx bx-plus" style={{ fontSize: '1rem' }} />
                    </button>
                    <p>{translate('Add new text')}</p>
                  </div>
                </div>
                <div className="container-side-column">
                  <SendCard isLoading={isSending} />
                </div>
              </div>
            </form>
          </FormProvider>
        )}
      </div>
    </GuidelineLayout>
  );
};

CreateGuideline.displayName = 'Create Guideline Page';

export default CreateGuideline;
